<template>
  <!-- Download and Upload Buttons -->
  <div class="d-flex gap-3 mb-6">
    <v-btn
      color="primary"
      variant="outlined"
      :loading="isDownloading"
      :disabled="isDownloading"
      @click="handleDownload"
      class="mr-4"
    >
      <v-icon start>mdi-download</v-icon>
      {{ t(strings.download) }}
    </v-btn>
    <v-btn
      color="primary"
      :loading="uploadMutation.isPending.value"
      :disabled="uploadMutation.isPending.value"
      @click="triggerFileUpload"
    >
      <v-icon start>mdi-upload</v-icon>
      {{ t(strings.uploadButton) }}
    </v-btn>
  </div>
  <input
    ref="fileInput"
    type="file"
    accept=".csv"
    style="display: none"
    @change="onFileSelected"
  />
  <!-- Loading skeleton -->
  <div v-if="loading" class="mb-4">
    <v-skeleton-loader
      type="table-heading, table-thead, table-tbody, table-tbody, table-tbody"
      class="elevation-1"
    ></v-skeleton-loader>
  </div>

  <v-alert v-else-if="error" type="error" variant="tonal" class="mb-4">
    {{ getTanstackErrorMessage(error) }}
  </v-alert>

  <v-alert
    v-else-if="sources.length === 0"
    type="info"
    variant="tonal"
    class="mb-4"
  >
    {{ t(strings.sourcesEmpty) }}
  </v-alert>

  <!-- Sources Table -->
  <v-data-table
    v-else
    :headers="tableHeaders"
    :items="sources"
    :loading="loading"
    class="elevation-1"
  >
    <template #item.file_original_name="{ item }">
      <span class="text-truncate" style="max-width: 200px">
        {{ item.file_original_name }}
      </span>
    </template>

    <template #item.created_at="{ item }">
      {{ formatDate(item.created_at) }}
    </template>

    <template #item.size="{ item }">
      {{ formatFileSize(item.size) }}
    </template>

    <template #item.actions="{ item }">
      <v-btn
        color="error"
        variant="text"
        size="small"
        :loading="deleteMutation.isPending.value && deletingId === item.id"
        :disabled="deleteMutation.isPending.value"
        @click="confirmDelete(item)"
      >
        <v-icon>mdi-delete</v-icon>
        {{ t(strings.delete) }}
      </v-btn>
    </template>
  </v-data-table>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showDeleteDialog" max-width="400px">
    <v-card>
      <v-card-title>{{ t(strings.deleteConfirmTitle) }}</v-card-title>
      <v-card-text>
        {{ t(strings.deleteConfirmMessage) }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="grey" variant="text" @click="showDeleteDialog = false">
          {{ t(strings.cancel) }}
        </v-btn>
        <v-btn
          color="error"
          variant="flat"
          :loading="deleteMutation.isPending.value"
          :disabled="deleteMutation.isPending.value"
          @click="handleDelete"
        >
          {{ t(strings.confirm) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { import_export_card_strings as strings } from "./ImportExportCard.strings.ts";
import { useReverseT } from "../../i18n.ts";
import {
  TanstackError,
  getTanstackErrorMessage,
  useDownload,
} from "@saflib/sdk";
import { showError } from "@saflib/vue";
import { useQuery } from "@tanstack/vue-query";
import type { PartSource } from "@vendata/power-up-spec";
import {
  useUploadCsvPartSourceMutation,
  useRemovePartSource,
  listPartSourceQuery,
} from "../../requests/part-sources/index.ts";

const { t } = useReverseT();

// File upload state
const selectedFile = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);

// Fetch sources data
const {
  data: sourcesData,
  isLoading: loading,
  error,
} = useQuery(listPartSourceQuery());
const sources = computed(() => sourcesData.value?.sources || []);

// Download functionality
const { download: downloadParts, isDownloading } = useDownload({
  subdomain: "power-up",
  path: "/parts/download",
  filename: "parts.csv",
});

// Mutations
const uploadMutation = useUploadCsvPartSourceMutation();
const deleteMutation = useRemovePartSource();

// Delete dialog state
const showDeleteDialog = ref(false);
const deletingId = ref<string | null>(null);

// Table headers
const tableHeaders = computed(() => [
  { title: t(strings.filename), key: "file_original_name", sortable: true },
  { title: t(strings.uploadDate), key: "created_at", sortable: true },
  { title: t(strings.rowsCount), key: "rows_count", sortable: true },
  { title: t(strings.fileSize), key: "size", sortable: true },
  { title: t(strings.actions), key: "actions", sortable: false },
]);

// File upload trigger
const triggerFileUpload = () => {
  fileInput.value?.click();
};

// File selection handler
const onFileSelected = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    selectedFile.value = target.files[0];
    handleUpload();
  }
};

// Upload handler
const handleUpload = async () => {
  if (!selectedFile.value) return;

  try {
    await uploadMutation.mutateAsync({ file: selectedFile.value });
    selectedFile.value = null;
    // Reset file input
    if (fileInput.value) fileInput.value.value = "";
  } catch (error) {
    if (error instanceof TanstackError) {
      showError(getTanstackErrorMessage(error));
    } else {
      showError(
        error instanceof Error ? error.message : t(strings.uploadError),
      );
    }
  }
};

// Download handler
const handleDownload = async () => {
  try {
    await downloadParts();
  } catch (error) {
    showError(t(strings.networkError));
  }
};

// Delete confirmation
const confirmDelete = (source: PartSource) => {
  deletingId.value = source.id;
  showDeleteDialog.value = true;
};

// Delete handler
const handleDelete = async () => {
  if (!deletingId.value) return;

  try {
    await deleteMutation.mutateAsync(deletingId.value);
    showDeleteDialog.value = false;
    deletingId.value = null;
  } catch (error) {
    if (error instanceof TanstackError) {
      showError(getTanstackErrorMessage(error));
    } else {
      showError(error instanceof Error ? error.message : t(strings.error));
    }
  }
};

// Utility functions
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString();
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
</script>
